import React, { useRef, useState, useEffect } from "react";
import { UploadCloud, X, CheckCircle, FileText } from "lucide-react";
import { Button } from "./Button";
import { cn } from "../lib/cn";
import { compressAndReadImage } from "./MultiImageUpload";

export interface DocumentUploadProps {
  label: string;
  subtitle?: string;
  accept?: string;
  value?: string | null;
  onUpload?: (file: File) => Promise<string>;
  onSuccess: (url: string) => void;
  className?: string;
}

export function DocumentUpload({
  label,
  subtitle,
  accept = "image/*,application/pdf",
  value,
  onUpload,
  onSuccess,
  className,
}: DocumentUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(value || null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setUploadedUrl(value || null);
  }, [value]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setErrorMsg(null);
    try {
      const url = onUpload
        ? await onUpload(file)
        : await compressAndReadImage(file, 1200, 0.82);
      setUploadedUrl(url);
      onSuccess(url);
    } catch (error) {
      console.error("Upload failed", error);
      setErrorMsg("Failed to upload document. Please try a different photo.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setUploadedUrl(null);
    setErrorMsg(null);
    onSuccess("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const isImage = uploadedUrl && (uploadedUrl.startsWith("data:image/") || uploadedUrl.startsWith("http"));

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-bold text-foreground">{label}</label>
        {subtitle && <span className="text-2xs text-muted-foreground">{subtitle}</span>}
      </div>

      {errorMsg && (
        <p className="text-2xs text-destructive font-medium">{errorMsg}</p>
      )}

      {!uploadedUrl ? (
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-border/80 hover:border-primary/60 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer bg-muted/20 hover:bg-muted/40 transition-all text-center group"
        >
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:scale-105 transition-transform">
            <UploadCloud className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-bold text-foreground">Click to upload photo or document</p>
            <p className="text-2xs text-muted-foreground mt-0.5">
              Shop Board / Front Photo, GST, FSSAI, Trade License (JPG, PNG)
            </p>
          </div>
          {isUploading && (
            <p className="text-2xs text-primary font-semibold animate-pulse">
              Compressing & optimizing image...
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between p-2.5 border rounded-xl bg-emerald-500/10 border-emerald-500/30 text-emerald-950 dark:text-emerald-100">
          <div className="flex items-center gap-2.5 min-w-0">
            {isImage ? (
              <img
                src={uploadedUrl}
                alt={label}
                className="w-10 h-10 rounded-lg object-cover border border-emerald-500/40 shadow-xs shrink-0 bg-background"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            )}
            <div className="min-w-0">
              <span className="text-xs font-bold flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                Document Attached
              </span>
              <p className="text-2xs text-muted-foreground truncate max-w-[200px]">
                Ready for verification review
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive shrink-0 rounded-lg"
            title="Remove document"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      <input
        type="file"
        ref={inputRef}
        className="hidden"
        accept={accept}
        onChange={handleFileChange}
      />
    </div>
  );
}
